import React, { useEffect, useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { getAllWorkouts } from '../../features/customTrainings/services/customTrainingsService';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery, Box } from '@mui/material';
import SearchAndFilters from '../../features/customTrainings/components/SearchAndFilters';
import WorkoutsList from '../../features/customTrainings/components/WorkoutsList';
import StackedWorkouts from '../../features/customTrainings/components/StackedWorkouts';
import CircularProgress from '../../components/CircularProgress';
import { FormattedMessage } from 'react-intl';

const CreateCustomTrainingPage = () => {
  const [workouts, setWorkouts] = useState([]);
  const [filteredWorkouts, setFilteredWorkouts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [equipmentFilter, setEquipmentFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    async function fetchWorkouts() {
      setLoading(true);
      try {
        const data = await getAllWorkouts();

        const updatedData = data.map((workout) => ({
          ...workout,
          id: Number(workout.id),
        }));

        setWorkouts(updatedData);
        setFilteredWorkouts(updatedData);

        const uniqueCategories = [...new Set(updatedData.map((workout) => workout.categoryName))];
        const uniqueEquipment = ['Bez opreme', 'Sa opremom'];

        setCategories(uniqueCategories);
        setEquipment(uniqueEquipment);
      } catch (error) {
        enqueueSnackbar(<FormattedMessage id="customTrainings.workouts.get.msg.error" defaultMessage="Workouts fetch failed" />, { variant: 'error' });
        setLoading(false);
      } finally {
        setLoading(false);
      }
    }
    fetchWorkouts();
  }, [enqueueSnackbar]);

  const handleSearch = useCallback((query) => setSearchQuery(query), []);
  const handleFilterChange = (type, value) => {
    if (type === 'category') setCategoryFilter(value);
    if (type === 'equipment') setEquipmentFilter(value);
  };

  useEffect(() => {
    let filtered = workouts.filter((workout) =>
      workout.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (categoryFilter) filtered = filtered.filter((w) => w.categoryName === categoryFilter);
    if (equipmentFilter) {
      if (equipmentFilter === 'Bez opreme') {
        filtered = filtered.filter((workout) => !workout.equipmentNeeded);
      } else if (equipmentFilter === 'Sa opremom') {
        filtered = filtered.filter((workout) => workout.equipmentNeeded);
      }
    }

    setFilteredWorkouts(filtered);
  }, [searchQuery, categoryFilter, equipmentFilter, workouts]);

  const handleItemAdded = (newItem) => {
    setWorkouts((prevWorkouts) => {
      const existingWorkoutIndex = prevWorkouts.findIndex((workout) => workout.id === newItem.id);

      if (existingWorkoutIndex !== -1) {
        const updatedWorkouts = [...prevWorkouts];
        updatedWorkouts[existingWorkoutIndex] = newItem;
        return updatedWorkouts;
      } else {
        return [...prevWorkouts, newItem];
      }
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: isSmallScreen ? 'column' : 'row' }}>
      <div style={{ flex: 1, padding: '20px', maxWidth: '400px' }}>
        <SearchAndFilters
          searchQuery={searchQuery}
          onSearch={handleSearch}
          categoryFilter={categoryFilter}
          onCategoryChange={(value) => handleFilterChange('category', value)}
          equipmentFilter={equipmentFilter}
          onEquipmentChange={(value) => handleFilterChange('equipment', value)}
          categories={categories}
          equipment={equipment}
        />
        <WorkoutsList workouts={filteredWorkouts} />
      </div>
      <div style={{ flex: 1, padding: '20px' }}>
        <StackedWorkouts onItemAdded={handleItemAdded} />
      </div>
    </div>
  );
};

export default CreateCustomTrainingPage;
